import React, { useCallback, useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { dashboardApi, WatchTimeData } from '../../../services/admin/dashboardApi';
import { AdminCard } from '../common/AdminPrimitives';

interface ChartDataPoint {
  x: number;
  y: number;
  date: string;
}

const formatWatchTime = (minutes: number): string => {
  if (minutes < 1) {
    return `${Math.round(minutes * 60)} sec`;
  }

  const totalMinutes = Math.floor(minutes);
  const seconds = Math.round((minutes - totalMinutes) * 60);
  const hours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;

  if (hours > 0) {
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  }

  return seconds > 0 ? `${remainingMinutes}m ${seconds}s` : `${remainingMinutes}m`;
};

const scaleY = (value: number, maxValue: number, minValue = 0): number => {
  const svgHeight = 209 - 20;
  const range = maxValue - minValue || 1;
  return 209 - (((value - minValue) / range) * svgHeight);
};

const scaleX = (index: number, totalPoints: number, maxWidth = 624): number => {
  if (totalPoints <= 1) {
    return maxWidth / 2;
  }

  return (index / (totalPoints - 1)) * maxWidth;
};

const generatePath = (data: ChartDataPoint[], maxValue: number): string => {
  if (data.length === 0) {
    return '';
  }

  const scaledPoints = data.map((point, index) => ({
    x: scaleX(index, data.length),
    y: scaleY(point.y, maxValue),
  }));

  if (scaledPoints.length === 1) {
    const point = scaledPoints[0];
    return `M ${point.x - 24} ${point.y} L ${point.x + 24} ${point.y}`;
  }

  let path = `M ${scaledPoints[0].x} ${scaledPoints[0].y}`;

  for (let index = 1; index < scaledPoints.length; index += 1) {
    const prevPoint = scaledPoints[index - 1];
    const currentPoint = scaledPoints[index];
    const controlPointX = (prevPoint.x + currentPoint.x) / 2;

    path += ` C ${controlPointX} ${prevPoint.y}, ${controlPointX} ${currentPoint.y}, ${currentPoint.x} ${currentPoint.y}`;
  }

  return path;
};

const generateAreaPath = (data: ChartDataPoint[], maxValue: number): string => {
  if (data.length === 0) {
    return '';
  }

  if (data.length === 1) {
    const x = scaleX(0, 1);
    const y = scaleY(data[0].y, maxValue);
    return `M ${x - 24} ${y} L ${x + 24} ${y} L ${x + 24} 229 L ${x - 24} 229 Z`;
  }

  const linePath = generatePath(data, maxValue);
  const lastX = scaleX(data.length - 1, data.length);
  return `${linePath} L ${lastX} 229 L 0 229 Z`;
};

const DashboardGraphs: React.FC = () => {
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [chartLoading, setChartLoading] = useState(true);
  const [chartError, setChartError] = useState<string | null>(null);
  const [courseData, setCourseData] = useState<WatchTimeData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    data: ChartDataPoint;
  } | null>(null);
  const [labelTooltip, setLabelTooltip] = useState<{ x: number; y: number; text: string } | null>(null);

  const maxChartValue = chartData.length > 0 ? Math.max(...chartData.map((item) => item.y)) : 0;
  const chartMaxValue = Math.max(maxChartValue, 10);

  const getYAxisLabels = (maxValue: number) => {
    const step = maxValue / 4;
    return [maxValue, +(step * 3).toFixed(1), +(step * 2).toFixed(1), +(step * 1).toFixed(1), 0];
  };

  const getDateRange = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);

    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      startDate: formatDate(thirtyDaysAgo),
      endDate: formatDate(today),
    };
  };

  const getDateRangeText = () => {
    const { startDate, endDate } = getDateRange();
    const formatDisplayDate = (dateStr: string) => {
      const date = new Date(dateStr);
      return `${date.getMonth() + 1}/${date.getDate()}`;
    };

    return `${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}`;
  };

  const fetchDailyActiveUsers = useCallback(async () => {
    try {
      setChartLoading(true);
      setChartError(null);

      const { startDate, endDate } = getDateRange();
      const data = await dashboardApi.getDailyActiveUsers(startDate, endDate);

      if (!data || data.length === 0) {
        setChartData([]);
        return;
      }

      const sortedData = [...data].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      setChartData(
        sortedData.map((point, index) => ({
          x: index,
          y: point.count,
          date: new Date(point.date).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' }),
        })),
      );
    } catch {
      setChartError('일일 활성 사용자 데이터를 불러오는데 실패했습니다.');
      setChartData([]);
    } finally {
      setChartLoading(false);
    }
  }, []);

  const fetchCourseWatchTime = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const raw = await dashboardApi.getAverageWatchTimeByCourse();
      setCourseData(Array.isArray(raw) ? raw : []);
    } catch {
      setError('강의 시청 시간 데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDailyActiveUsers();
    fetchCourseWatchTime();
  }, [fetchDailyActiveUsers, fetchCourseWatchTime]);

  const getBarWidth = (minutes: number): string => {
    if (courseData.length === 0) {
      return '0%';
    }

    const maxMinutes = Math.max(...courseData.map((course) => course.averageWatchTime));
    if (maxMinutes === 0) {
      return '10%';
    }

    return `${Math.max(5, (minutes / maxMinutes) * 100)}%`;
  };

  return (
    <Grid>
      <Panel>
        <PanelHeader>
          <PanelTitle>일일 활성 사용자 수</PanelTitle>
          <PanelSubtitle>{getDateRangeText()}</PanelSubtitle>
        </PanelHeader>
        <GraphPanel>
          {getYAxisLabels(chartMaxValue).map((label, index) => (
            <YAxisRow key={index}>
              <YAxisLabel>{label}</YAxisLabel>
              <YAxisDivider />
            </YAxisRow>
          ))}

          <GraphOverlay>
            {chartLoading ? (
              <Placeholder>일일 활성 사용자 데이터를 불러오는 중...</Placeholder>
            ) : chartError ? (
              <Placeholder>
                <div>{chartError}</div>
                <RetryButton type="button" onClick={fetchDailyActiveUsers}>
                  다시 시도
                </RetryButton>
              </Placeholder>
            ) : chartData.length === 0 ? (
              <Placeholder>해당 기간의 일일 활성 사용자 데이터가 없습니다.</Placeholder>
            ) : (
              <ChartSvg viewBox="0 0 624 229">
                <defs>
                  <linearGradient id="dashboardAreaGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#C5E0E0" stopOpacity="0.8" />
                    <stop offset="50%" stopColor="#C5E0E0" stopOpacity="0.5" />
                    <stop offset="100%" stopColor="#C5E0E0" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <ChartAreaPath d={generateAreaPath(chartData, chartMaxValue)} fill="url(#dashboardAreaGradient)" />
                <ChartLinePath
                  d={generatePath(chartData, chartMaxValue)}
                  fill="none"
                  stroke="#448181"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {chartData.map((point, index) => {
                  const svgX = scaleX(index, chartData.length);
                  const svgY = scaleY(point.y, chartMaxValue);

                  return (
                    <g key={index}>
                      <ChartPoint cx={svgX} cy={svgY} r="3" />
                      <circle
                        cx={svgX}
                        cy={svgY}
                        r="8"
                        fill="transparent"
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={(event) => {
                          setTooltip({
                            visible: true,
                            x: event.clientX,
                            y: event.clientY,
                            data: point,
                          });
                        }}
                        onMouseLeave={() => setTooltip(null)}
                      />
                    </g>
                  );
                })}
              </ChartSvg>
            )}
          </GraphOverlay>
        </GraphPanel>
      </Panel>

      <Panel>
        <PanelHeader>
          <PanelTitle>강의별 평균 시청 시간</PanelTitle>
          <PanelSubtitle>상위 5개 강의</PanelSubtitle>
        </PanelHeader>

        {loading ? (
          <CenterMessage>강의 데이터를 불러오는 중...</CenterMessage>
        ) : error ? (
          <CenterMessage>
            <div>{error}</div>
            <RetryButton type="button" onClick={fetchCourseWatchTime}>
              Retry
            </RetryButton>
          </CenterMessage>
        ) : (
          <BarList>
            {courseData.map((course, index) => (
              <BarRow key={course.courseId}>
                <BarLabel
                  title={course.courseTitle}
                  onMouseEnter={(event) =>
                    setLabelTooltip({ x: event.clientX, y: event.clientY, text: course.courseTitle })
                  }
                  onMouseMove={(event) =>
                    setLabelTooltip((prev) => (prev ? { ...prev, x: event.clientX, y: event.clientY } : prev))
                  }
                  onMouseLeave={() => setLabelTooltip(null)}
                >
                  {course.courseTitle}
                </BarLabel>
                <BarTrack>
                  <BarFill style={{ width: getBarWidth(course.averageWatchTime), animationDelay: `${index * 0.15}s` }} />
                </BarTrack>
                <BarValue>{formatWatchTime(course.averageWatchTime)}</BarValue>
              </BarRow>
            ))}
            {Array.from({ length: Math.max(0, 5 - courseData.length) }).map((_, index) => (
              <BarRow key={`empty-${index}`}>
                <BarLabel>-</BarLabel>
                <BarTrack>
                  <EmptyBar />
                </BarTrack>
                <BarValue>0m</BarValue>
              </BarRow>
            ))}
          </BarList>
        )}
      </Panel>

      {tooltip?.visible && (
        <Tooltip style={{ left: tooltip.x, top: tooltip.y - 60 }}>
          <div>{tooltip.data.date}</div>
          <TooltipMuted>{tooltip.data.y}명</TooltipMuted>
        </Tooltip>
      )}

      {labelTooltip && (
        <Tooltip style={{ left: labelTooltip.x, top: labelTooltip.y - 36 }}>{labelTooltip.text}</Tooltip>
      )}
    </Grid>
  );
};

const drawLine = keyframes`
  from { stroke-dashoffset: 10000; }
  to { stroke-dashoffset: 0; }
`;

const fillArea = keyframes`
  from { opacity: 0; transform: scaleY(0); }
  to { opacity: 1; transform: scaleY(1); }
`;

const growBar = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;

const Grid = styled.div`
  position: relative;
  display: grid;
  width: 100%;
  grid-template-columns: minmax(0, 725px) minmax(320px, 1fr);
  gap: 16px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled(AdminCard)`
  display: flex;
  min-height: 304px;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const PanelTitle = styled.div`
  color: #171719;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
`;

const PanelSubtitle = styled.div`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.43;
`;

const GraphPanel = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 32px;
  color: #7d7d7d;
  font-size: 12px;
`;

const YAxisRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const YAxisLabel = styled.div`
  width: 32px;
  color: rgba(55, 56, 60, 0.61);
  font-size: 12px;
  font-weight: 500;
`;

const YAxisDivider = styled.div`
  flex: 1;
  height: 1px;
  background: rgba(112, 115, 124, 0.22);
`;

const GraphOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 42px;
  right: 0;
  bottom: 0;
`;

const ChartSvg = styled.svg`
  width: 100%;
  height: 229px;
`;

const ChartLinePath = styled.path`
  stroke-dasharray: 10000;
  stroke-dashoffset: 10000;
  animation: ${drawLine} 2.5s ease-out forwards;
`;

const ChartAreaPath = styled.path`
  transform-origin: bottom;
  opacity: 0;
  animation: ${fillArea} 1.8s ease-in-out 1.2s forwards;
`;

const ChartPoint = styled.circle`
  fill: #448181;
`;

const Placeholder = styled.div`
  display: flex;
  height: 229px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #7d7d7d;
  font-size: 14px;
  text-align: center;
`;

const RetryButton = styled.button`
  padding: 8px 14px;
  border-radius: 8px;
  background: #448181;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
`;

const CenterMessage = styled.div`
  display: flex;
  min-height: 208px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #7d7d7d;
  text-align: center;
`;

const BarList = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  gap: 12px;
`;

const BarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BarLabel = styled.div`
  width: 84px;
  overflow: hidden;
  color: #171719;
  font-size: 12px;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BarTrack = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: flex-end;
  padding: 4px 0;
`;

const BarFill = styled.div`
  height: 12px;
  border-radius: 6px;
  background: #5c9a9a;
  transform-origin: left;
  animation: ${growBar} 1.2s ease-out both;
`;

const EmptyBar = styled.div`
  width: 5%;
  height: 12px;
  border-radius: 6px;
  background: #e5e5e5;
  opacity: 0.3;
`;

const BarValue = styled.div`
  width: 72px;
  color: #171719;
  font-size: 12px;
  font-weight: 500;
  text-align: right;
`;

const Tooltip = styled.div`
  position: fixed;
  z-index: 9999;
  transform: translateX(-50%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.9);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 8px 12px;
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  pointer-events: none;
  white-space: nowrap;
`;

const TooltipMuted = styled.div`
  color: #c5e0e0;
  font-size: 11px;
`;

export default DashboardGraphs;
